import { Family as FamilyProto } from "protogen/advisors_service_pb";
import { Box, Collapse, Typography, IconButton } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import useIsMobile from "components/hooks/useIsMobile";
import { useGetFamilyCalendarIntegrations } from "services/calendar";
import { calendarSyncStatus } from "components/calendar/integrations/CalendarSyncStatus";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DropdownMenu from "components/common/DropdownMenu";
import {
  ImportCalendar,
  ADVISOR_FAMILY_IMPORT_CALENDARS,
} from "components/calendar/integrations/imports";
import CalendarFlow from "components/calendar/integrations/ImportCalendarFlow";

interface Calendar {
  id: string;
  summary: string;
}

const ImportedCalendars = ({
  integrations,
  family,
}: {
  integrations: any[];
  family: FamilyProto;
}) => {
  const isMobile = useIsMobile();
  const [expandedIntegration, setExpandedIntegration] = useState<
    string | undefined
  >();
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);
  const [importType, setImportType] = useState<ImportCalendar | null>(null);

  const getAccessLevel = (scope: string) => {
    if (scope === "freebusy") {
      return { label: "Free/Busy" };
    } else if (scope === "read_write") {
      return { label: "Read and write" };
    } else {
      return { label: "Read only" };
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setIsSectionExpanded(!isSectionExpanded)}
      >
        {isSectionExpanded ? (
          <ChevronUp size={20} />
        ) : (
          <ChevronDown size={20} />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography variant="bodyHeavy">Imported calendars</Typography>
          <DropdownMenu
            size="small"
            noIcon={true}
            variant="text"
            fullWidth
            buttonContent="Add a calendar"
            options={ADVISOR_FAMILY_IMPORT_CALENDARS.map((c) => ({
              title: c.title,
              onClick: () => setImportType(c),
            }))}
          />
        </Box>
      </Box>
      <Collapse in={isSectionExpanded}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            gap: "16px",
            padding: !isMobile ? "12px 0" : undefined,
          }}
        >
          {integrations.map((integration) => {
            const [icon, byline] = calendarSyncStatus(
              integration.status as "active" | "deactivated",
              Number(integration.lastAccessedSec),
              integration.type,
            );
            const isDeactivated = integration.status === "deactivated";
            const isExpanded = expandedIntegration === integration.ref;
            return (
              <Box
                key={integration.ref}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "16px",
                  border: "1px solid #E2E3E4",
                  borderRadius: "8px",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    {!isDeactivated && icon}
                    <Box>
                      <Typography variant="bodyHeavy">
                        {integration.name}
                      </Typography>
                      {isDeactivated ? (
                        <Typography variant="bodySmall" color="text.secondary">
                          Deactivated
                        </Typography>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="bodySmall"
                            color="text.secondary"
                          >
                            {byline}
                          </Typography>
                          {getAccessLevel(integration.accessScope || "") && (
                            <Typography
                              variant="bodySmall"
                              color="text.secondary"
                            >
                              •{" "}
                              {getAccessLevel(integration.accessScope || "")
                                .label || "Read only"}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {!isDeactivated && integration.calendars?.length > 0 && (
                    <IconButton
                      onClick={() =>
                        setExpandedIntegration((ref) =>
                          ref === integration.ref ? undefined : integration.ref,
                        )
                      }
                      aria-label="expand"
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </Box>
                <Collapse in={isExpanded && !isDeactivated}>
                  {integration.calendars?.length > 0 && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="16px"
                      marginTop="12px"
                    >
                      {integration.calendars.map((cal: Calendar) => (
                        <Box
                          key={cal.id}
                          display="flex"
                          alignItems="center"
                          gap="8px"
                        >
                          <Box
                            display="flex"
                            flexDirection="column"
                            width="100%"
                          >
                            <Typography
                              variant="bodyHeavy"
                              color="text.primary"
                              sx={{
                                textWrap: "none",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                width: "100%",
                              }}
                            >
                              {cal.summary}
                            </Typography>
                            <Typography
                              variant="body"
                              color="text.secondary"
                              sx={{
                                textWrap: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                width: "100%",
                              }}
                            >
                              {cal.id}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Collapse>
              </Box>
            );
          })}
          {!integrations.length && (
            <Typography
              variant="body"
              sx={{ color: "#6B6E7B", marginTop: isMobile ? "16px" : "" }}
            >
              No calendars connected
            </Typography>
          )}
        </Box>
      </Collapse>
      {importType && (
        <CalendarFlow
          importType={importType}
          open={!!importType}
          onClose={() => setImportType(null)}
          onSuccess={async () => {
            setImportType(null);
          }}
          familyRef={family.ref}
        />
      )}
    </Box>
  );
};

const ExportedCalendars = ({ integrations }: { integrations: any[] }) => {
  const isMobile = useIsMobile();
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setIsSectionExpanded(!isSectionExpanded)}
      >
        {isSectionExpanded ? (
          <ChevronUp size={20} />
        ) : (
          <ChevronDown size={20} />
        )}
        <Typography variant="bodyHeavy">Exported calendars</Typography>
      </Box>
      <Collapse in={isSectionExpanded}>
        <Box
          sx={{
            gap: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {integrations.map((integration) => {
            const [icon, byline] = calendarSyncStatus(
              integration.status as "active" | "error" | "deactivated",
              Number(integration.lastAccessedSec),
            );
            return (
              <Box
                key={integration.ref}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  padding: "16px",
                  border: "1px solid #E2E3E4",
                  borderRadius: "8px",
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  {icon}
                  <Box>
                    <Typography variant="bodyHeavy">
                      {integration.name || "Exported Calendar"}
                    </Typography>
                    <Typography variant="bodySmall" color="text.secondary">
                      {byline}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
          {!integrations.length && (
            <Typography
              variant="body"
              sx={{ color: "#6B6E7B", marginTop: isMobile ? "16px" : "" }}
            >
              No calendars connected
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const DefaultCalendarSettings = ({
  integrations,
  family,
}: {
  integrations: any[];
  family: FamilyProto;
}) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(false);

  const defaultCalendarName = useMemo(() => {
    if (!family.defaultCalendarIntegrationRef || !family.defaultCalendarId) {
      return "Faye calendar";
    }
    const integration = integrations.find(
      (i) => i.ref === family.defaultCalendarIntegrationRef,
    );
    if (!integration) return "Faye calendar";
    const calendar = integration.calendars.find(
      (c: any) => c.id === family.defaultCalendarId,
    );
    return calendar ? calendar.summary : "Faye calendar";
  }, [family, integrations]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setIsSectionExpanded(!isSectionExpanded)}
      >
        {isSectionExpanded ? (
          <ChevronUp size={20} />
        ) : (
          <ChevronDown size={20} />
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Typography variant="bodyHeavy">Default calendar</Typography>
          <Typography variant="body" color="text.secondary">
            {defaultCalendarName}
          </Typography>
        </Box>
      </Box>
      <Collapse in={isSectionExpanded}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Typography variant="body" color="text.secondary">
            The calendar used by default when creating new events.
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ({ family }: { family: FamilyProto }) => {
  const { request, data } = useGetFamilyCalendarIntegrations();
  const [localFamily, setLocalFamily] = useState<FamilyProto>(family);

  useEffect(() => {
    request({
      familyRef: family.ref,
    });
  }, []);

  useEffect(() => {
    setLocalFamily(family);
  }, [family]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Typography variant="h3">Calendar settings</Typography>
      <DefaultCalendarSettings
        integrations={data?.importIntegrations || []}
        family={localFamily}
      />
      <ImportedCalendars
        integrations={data?.importIntegrations || []}
        family={localFamily}
      />
      <ExportedCalendars integrations={data?.exportIntegrations || []} />
    </Box>
  );
};
